// Deals constants — single source of truth. Stage keys MUST match the backend
// `DEAL_STAGES` (server.js) exactly.

import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { DealStage, AssignmentStatus } from './types';

/** Badge meta for a deal hand-off status (none = no badge). */
export const ASSIGNMENT_META: Record<Exclude<AssignmentStatus, 'none'>, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pending', tone: 'warning' },
  accepted: { label: 'Accepted', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
};

export interface StageMeta {
  key: DealStage;
  label: string;
  tone: BadgeTone;
}

/**
 * NOT the source of truth for VALID stages — Deal.stage (and
 * Lead.pipeline_stage) validate against the live 'stage' Master Data list
 * (src/db-masters.js, seeded by scripts/seed-deal-stages.mjs, admin-editable
 * from /master-data → Sales & CRM → Pipeline Stages); server.js's
 * isValidDealStage() checks that list dynamically, not a hardcoded array.
 * useDealStages() below reads that same live list and is what every stage
 * PICKER (dropdown, Kanban columns) actually renders.
 *
 * This array still does two real jobs, though:
 *   1. The fallback useDealStages() returns before the master has ever been
 *      seeded (first-run only — in a seeded system it never surfaces).
 *   2. The 'prospecting' / 'qualification' / 'proposal' entries are the
 *      LEGACY stage codes server.js's LEGACY_DEAL_STAGES still accepts
 *      indefinitely, because old deals' closed/won-lost history still reads
 *      them. STAGE_LABELS/STAGE_TONES below are built from this static list
 *      (not from the live master), so an old deal carrying one of those
 *      codes still resolves to a real label instead of the raw code — that
 *      is deliberate, not drift, and removing them would break that lookup.
 */
export const DEAL_STAGES: StageMeta[] = [
  { key: 'new_lead', label: 'New Lead', tone: 'neutral' },
  { key: 'contacted', label: 'Contacted', tone: 'neutral' },
  { key: 'prospecting', label: 'Prospecting', tone: 'neutral' },
  { key: 'qualification', label: 'Qualification', tone: 'info' },
  { key: 'qualified', label: 'Qualified', tone: 'info' },
  { key: 'requirement_received', label: 'Requirement Received', tone: 'info' },
  { key: 'proposal', label: 'Proposal', tone: 'primary' },
  { key: 'quotation_sent', label: 'Quotation Sent', tone: 'primary' },
  { key: 'follow_up', label: 'Follow Up', tone: 'primary' },
  { key: 'negotiation', label: 'Negotiation', tone: 'warning' },
  { key: 'sample_trial', label: 'Sample / Trial', tone: 'warning' },
  { key: 'trial_successful', label: 'Trial Successful', tone: 'warning' },
  { key: 'trial_unsuccessful', label: 'Trial Unsuccessful', tone: 'danger' },
  { key: 'po_expected', label: 'PO Expected', tone: 'primary' },
  { key: 'order_confirmed', label: 'Order Confirmed', tone: 'primary' },
  { key: 'production', label: 'Production', tone: 'info' },
  { key: 'dispatch', label: 'Dispatch', tone: 'info' },
  { key: 'payment_pending', label: 'Payment Pending', tone: 'warning' },
  { key: 'closed_won', label: 'Closed Won', tone: 'success' },
  { key: 'closed_lost', label: 'Closed Lost', tone: 'danger' },
];

export const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  DEAL_STAGES.map((s) => [s.key, s.label]),
);

export const STAGE_TONES: Record<string, BadgeTone> = Object.fromEntries(
  DEAL_STAGES.map((s) => [s.key, s.tone]),
);

export const STAGE_OPTIONS = DEAL_STAGES.map((s) => ({ value: s.key, label: s.label }));

export const CLOSED_STAGES: DealStage[] = ['closed_won', 'closed_lost'];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const PRIORITY_TONES: Record<string, BadgeTone> = {
  low: 'neutral',
  medium: 'info',
  high: 'danger',
};

/** Selectable currencies — the same full list the quotation / proforma /
 *  invoice form bills in (INR first, common export currencies, then the rest),
 *  so a record's currency always carries over to its documents. */
export { CURRENCY_OPTIONS } from '@/shared/constants/currencies';

/**
 * NOT the source of truth — Deal.source (and Lead.source) now read the
 * shared 'source' Master Data list (src/db-masters.js, seeded by
 * scripts/seed-lead-deal-sources.mjs, admin-editable from /master-data →
 * Sales & CRM → Sources) via useDealSources()/useLeadSources(). This is only
 * the fallback useDealSources() returns before that master has been seeded.
 */
export const DEAL_SOURCE_OPTIONS = [
  { value: 'indiamart', label: 'IndiaMART' },
  { value: 'justdial', label: 'JustDial' },
  { value: 'tradeindia', label: 'TradeIndia' },
  { value: 'website', label: 'Website / Inbound' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'direct', label: 'Direct / Walk-in' },
  { value: 'referral', label: 'Referral' },
  { value: 'cold_call', label: 'Cold Call / Outbound' },
  { value: 'exhibition', label: 'Exhibition / Expo' },
  { value: 'google_ads', label: 'Google Ads / SEO' },
  { value: 'email', label: 'Email Campaign' },
  { value: 'other', label: 'Other' },
] as const;

export const DEAL_SOURCE_LABELS: Record<string, string> = {
  indiamart: 'IndiaMART',
  justdial: 'JustDial',
  tradeindia: 'TradeIndia',
  website: 'Website',
  whatsapp: 'WhatsApp',
  direct: 'Direct',
  referral: 'Referral',
  cold_call: 'Cold Call',
  exhibition: 'Exhibition',
  google_ads: 'Google Ads',
  email: 'Email Campaign',
  other: 'Other',
};

export const DEAL_SOURCE_TONES: Record<string, BadgeTone> = {
  indiamart: 'warning',
  justdial: 'info',
  tradeindia: 'warning',
  website: 'success',
  whatsapp: 'success',
  direct: 'neutral',
  referral: 'primary',
  cold_call: 'neutral',
  exhibition: 'info',
  google_ads: 'primary',
  email: 'info',
  other: 'neutral',
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  AED: 'AED ',
  GBP: '£',
};

/**
 * Format a monetary amount with its OWN currency symbol. Currencies are never
 * aggregated across types — each value is rendered with its own currency.
 */
export function formatMoney(value: number | undefined | null, currency = 'INR'): string {
  const amount = Number(value ?? 0);
  const symbol = CURRENCY_SYMBOLS[currency];
  // `maximumFractionDigits` must be stated. Intl's default of 3 surfaces on any
  // derived figure that does not divide cleanly — an average deal size of
  // 680000/6 rendered as "₹1,13,333.333", three decimals of a currency whose
  // smallest unit is two. Whole amounts are unaffected: `minimumFractionDigits`
  // stays 0, so ₹6,80,000 does not become ₹6,80,000.00.
  const formatted = amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return symbol ? `${symbol}${formatted}` : `${currency} ${formatted}`;
}

